import React, { useEffect } from 'react';
import PropTypes from 'prop-types';

import config from '../../../config';
import Order from '../../../records/Order';
import TraceToken from '../../../helpers/TraceToken';
import { toPounds } from '../../../helpers/Currency';
import { loadScript } from '../../../helpers/ExternalScript';
import { isUsercentricsConsentGranted } from '../../../helpers/UsercentricsConsent';

export const CONTAINER_ID = 'sovendus-container-1';
export const SCRIPT_ID = 'sovendus-flexible-iframe';

export async function sha256Hex(input) {
    if (
        !input ||
        typeof window === 'undefined' ||
        !window.crypto ||
        !window.crypto.subtle ||
        typeof TextEncoder === 'undefined'
    ) {
        return null;
    }

    try {
        const bytes = new TextEncoder().encode(String(input));
        const buffer = await window.crypto.subtle.digest('SHA-256', bytes);
        return Array.from(new Uint8Array(buffer))
            .map(b => ('00' + b.toString(16)).slice(-2))
            .join('');
    } catch (e) {
        return null;
    }
}

function splitName(customerName = '') {
    const [first = '', ...rest] = String(customerName).trim().split(/\s+/).filter(Boolean);
    return { firstName: first, lastName: rest.join(' ') };
}

function sovendusConsentSpec() {
    const serviceId = config.get('usercentricsSovendusServiceId');
    return serviceId
        ? { serviceIds: [serviceId] }
        : { categorySlug: config.get('usercentricsSovendusCategory') };
}

export default function SovendusBanner({ order }) {
    useEffect(() => {
        let cancelled = false;

        async function initSovendus() {

            if (typeof document === 'undefined' || document.getElementById(SCRIPT_ID)) {
                return;
            }

            const sessionId = (await sha256Hex(TraceToken.sessionToken)) || '';
            if (cancelled || document.getElementById(SCRIPT_ID)) {
                return;
            }

            const { firstName, lastName } = splitName(order.customerName);
            const promoDiscount = order.getPromoCode();

            window.sovIframes = window.sovIframes || [];
            window.sovIframes.push({
                trafficSourceNumber: config.get('sovendusTrafficSourceNumber'),
                trafficMediumNumber: config.get('sovendusTrafficMediumNumber'),
                hasConsent: isUsercentricsConsentGranted(sovendusConsentSpec()),
                sessionId,
                orderId: order.get('id'),
                orderValue: toPounds(order.getTotalPrice()).toFixed(2),
                orderCurrency: 'GBP',
                usedCouponCode: order.getDiscountCode() || (promoDiscount && promoDiscount.promoCode) || '',
                iframeContainerId: CONTAINER_ID,
                integrationType: 'genericScript-1.5.0-hasConsent',
            });

            window.sovConsumer = {
                consumerSalutation: '',
                consumerFirstName: firstName,
                consumerLastName: lastName,
                consumerEmail: order.email || '',
                consumerStreet: '',
                consumerStreetNumber: '',
                consumerZipcode: '',
                consumerCity: '',
                consumerCountry: 'GB',
                consumerPhone: '',
                consumerYearOfBirth: '',
                consumerDateOfBirth: '',
            };

            loadScript(config.get('sovendusFlexibleIframeUrl'), SCRIPT_ID).catch(() => {
                // eslint-disable-next-line no-console
                console.info('Failed to load Sovendus');
            });
        }

        initSovendus();

        return () => {
            cancelled = true;
        };
    }, [order]);

    return (
        <div className="box--light box--rounded bg--white margin-bottom--sm margin-top--sm sovendus-banner">
            <div id={CONTAINER_ID} />
        </div>
    );
}

SovendusBanner.propTypes = {
    order: PropTypes.instanceOf(Order).isRequired,
};
